import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { applyTypography } from '@repo/common';
import { useT } from '@repo/common';
import { useAuth } from '@repo/common';
import {
  getDropdownItems,
  getGnbDropdownConfig,
  getNavItems,
} from './jobNavigationConfig';
import { LangSelector } from '@repo/common';
import plusIcon from '@repo/common/assets/icons/24px/icon_24px_plus.svg?url';
import minusIcon from '@repo/common/assets/icons/24px/icon_24px_minus.svg?url';
import { ReactComponent as ExternalArrow } from '@repo/common/assets/icons/16px/icon_16px_navigation-diagonal.svg';

const cx = (...classes) => classes.filter(Boolean).join(' ');
const underlineHeight = '0.7em';

function MobileMenuDrawer({ isOpen, onClose, activePath, onLoginRequired }) {
  const t = useT();
  const { isLoggedIn, user } = useAuth();
  const navItems = getNavItems(user?.role);
  const location = useLocation();

  const [expandedItems, setExpandedItems] = useState(new Set());
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const toggleExpanded = (label) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const currentPath = location?.pathname ?? '/';
  const currentTab = new URLSearchParams(location?.search ?? '').get('tab');

  const stripQuery = (href) => (href || '').split('?')[0];

  const hrefMatchesPath = (href) => {
    const path = stripQuery(href);
    if (!path) return false;
    if (path === '/') return currentPath === '/';
    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  const isSubItemActive = (href) => {
    const path = stripQuery(href);
    if (!path) return false;
    const itemTab = new URLSearchParams((href || '').split('?')[1] ?? '').get(
      'tab',
    );
    const pathMatches =
      path === '/'
        ? currentPath === '/'
        : currentPath === path || currentPath.startsWith(`${path}/`);
    return pathMatches && itemTab === currentTab;
  };

  const matchedNavItem = navItems.find(({ label, href }) => {
    if (hrefMatchesPath(href)) return true;
    const dropdowns = getDropdownItems(label, user?.role);
    return dropdowns.some(({ href: itemHref }) => hrefMatchesPath(itemHref));
  });
  // 마이페이지는 어느 GNB 메뉴에도 속하지 않으므로 밑줄을 표시하지 않는다
  const isMyPage = currentPath.startsWith('/mypage');
  const resolvedActiveHref = isMyPage
    ? null
    : (matchedNavItem?.href ?? activePath ?? null);

  const isActive = (href) => resolvedActiveHref === href;

  const navTypo = applyTypography('title2');

  return (
    <>
      {/* Overlay */}
      <div
        className={cx(
          'fixed inset-0 z-[990] bg-black/40 opacity-0 pointer-events-none transition-opacity duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none tabletH:block',
          isOpen && 'opacity-100 pointer-events-auto',
        )}
        onClick={onClose}
        aria-hidden='true'
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={cx(
          'fixed bottom-0 left-0 top-[30px] z-[995] w-full max-w-[376px] overflow-visible bg-grayscale-gray01 transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none  tabletH:block',
          isOpen
            ? 'translate-x-0 opacity-100'
            : '-translate-x-full opacity-0 pointer-events-none',
        )}
        role='dialog'
        aria-modal='true'
        aria-label='Mobile navigation menu'
      >
        <div className='flex h-full min-h-0 flex-col overflow-y-auto px-9 pb-[60px] pt-4 min-[360px]:px-4 min-[360px]:pt-6'>
          {navItems.map(({ label, href, external, tooltip }, index) => {
            const submenuSections = getGnbDropdownConfig(label, user?.role)?.sections ?? [];
            const hasSubmenu = submenuSections.some(
              (section) => section.items?.length > 0,
            );
            const isExpanded = expandedItems.has(label);
            const active = isActive(href);

            const triggerClassName = cx(
              'inline-flex items-center gap-1 px-1.5 relative leading-[150%] font-medium',
              'bg-[length:0%_var(--u-h)] bg-[position:0_120%] bg-no-repeat',
              'transition-[background-size,color] duration-sm ease motion-reduce:transition-none',
              'group-hover/menu-item:text-green-deep04 group-hover/menu-item:bg-[length:100%_var(--u-h)]',
              active
                ? 'text-green-deep04 bg-[length:100%_var(--u-h)]'
                : 'text-grayscale-gray09',
              navTypo,
            );
            const triggerStyle = {
              '--u-h': underlineHeight,
              backgroundImage: 'linear-gradient(0deg, rgba(0, 158, 79, 0.4) 0 var(--u-h), transparent var(--u-h) 100%)',
            };

            return (
              <div
                key={label}
                className={cx(
                  'pb-[10px]',
                  index !== navItems.length - 1 &&
                    'border-b-[1.6px] border-grayscale-gray02',
                )}
              >
                <div className='flex items-center justify-between pt-3'>
                  <div className='relative flex flex-col items-start pb-3 shrink-0 group/menu-item'>
                    {external ? (
                      <a
                        href={href}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={onClose}
                        className={triggerClassName}
                        style={triggerStyle}
                      >
                        {t(label)}
                        <ExternalArrow aria-hidden className='size-4 shrink-0' />
                      </a>
                    ) : (
                      <Link
                        to={href}
                        onClick={onClose}
                        className={triggerClassName}
                        style={triggerStyle}
                      >
                        {t(label)}
                      </Link>
                    )}
                    {tooltip?.description && (
                      <p className='typo-b12 mt-1 px-1.5 text-text-placeholder'>
                        {t(tooltip.description)}
                      </p>
                    )}
                  </div>

                  {hasSubmenu && (
                    <button
                      type='button'
                      onClick={() => toggleExpanded(label)}
                      className='flex items-center justify-center p-2 border-0 rounded-lg cursor-pointer bg-grayscale-gray01 shrink-0'
                      aria-label={
                        isExpanded
                          ? `Collapse ${label} submenu`
                          : `Expand ${label} submenu`
                      }
                      aria-expanded={isExpanded}
                    >
                      <img
                        src={isExpanded ? minusIcon : plusIcon}
                        alt=''
                        aria-hidden='true'
                        className='size-4'
                      />
                    </button>
                  )}
                </div>

                {hasSubmenu && (
                  <div
                    className={cx(
                      'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none',
                      isExpanded
                        ? 'grid-rows-[1fr] opacity-100'
                        : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div className='min-h-0 overflow-hidden'>
                      <div className='flex flex-col items-start gap-[18px] rounded-lg bg-grayscale-gray01 pb-[10px]'>
                        {submenuSections.map((section, sectionIndex) => (
                          <div
                            key={section.title || `${label}-${sectionIndex}`}
                            className='flex flex-col items-start w-full'
                          >
                            {section.title && (
                              <p
                                className={`${applyTypography('b11Strong', section.title)} px-3 pb-1 pt-2 text-text-placeholder`}
                              >
                                {t(section.title)}
                              </p>
                            )}
                            {section.items.map(
                              ({
                                label: itemLabel,
                                href: itemHref,
                                requireAuth,
                              }) => {
                                const submenuItemTypo = applyTypography(
                                  'b14Strong',
                                  itemLabel,
                                );

                                // 현재 페이지인 서브메뉴는 pressed(선택) 상태로 표시
                                const subItemActive = isSubItemActive(itemHref);

                                const itemClassName = cx(
                                  'flex h-[38px] w-full shrink-0 items-center px-3 py-[10px]',
                                  'transition-colors duration-sm ease motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-green-deep03',
                                  subItemActive
                                    ? 'bg-grayscale-gray01 text-green-deep03'
                                    : 'bg-grayscale-gray01 text-grayscale-gray07 hover:text-green-deep03 focus-visible:text-green-deep03',
                                  submenuItemTypo,
                                );

                                if (requireAuth && !isLoggedIn) {
                                  return (
                                    <button
                                      key={`${itemHref}-${itemLabel}`}
                                      type='button'
                                      onClick={() => {
                                        onClose();
                                        onLoginRequired?.();
                                      }}
                                      className={cx(
                                        itemClassName,
                                        'border-0 text-left',
                                      )}
                                    >
                                      {t(itemLabel)}
                                    </button>
                                  );
                                }

                                return (
                                  <Link
                                    key={`${itemHref}-${itemLabel}`}
                                    to={itemHref}
                                    onClick={onClose}
                                    aria-current={
                                      subItemActive ? 'page' : undefined
                                    }
                                    className={itemClassName}
                                  >
                                    {t(itemLabel)}
                                  </Link>
                                );
                              },
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className='mt-6 shrink-0'>
            <LangSelector mobileDrawer />
          </div>
        </div>
      </div>
    </>
  );
}

export default MobileMenuDrawer;
